// Pets, unlocked via the shop's Summon gacha (10 Shards per roll — see
// ShopSummonMenu.js). `spriteKey` looks up data/sprites.js's PET_SPRITES,
// currently all PLACEHOLDER ART; swap in real per-pet assets there before
// this ships publicly (see that file's comment).
//
// Every pet belongs to a themed category and has exactly one pet per rarity
// tier within its base category. Rarity drives both the summon roll
// weighting below and the gallery/case-reveal border treatment (see
// style.css's .pet-slot-rarity-* classes).
//
// Pets are EQUIPPABLE, not passive: only pets in save.equippedPets grant
// their bonus (see getPetBonuses below and Game.js), one slot by default,
// two with the "Bonded Companion" Shard unlock (data/shardUnlocks.js).
// Pulling a duplicate of an owned pet levels it up instead of instantly
// becoming essence — see MAX_COPY_LEVEL/getCopyMultiplier and
// SaveManager.recordPetPull. Both stat and unique bonuses scale with copy level.
//
// DESIGN RULE — one distinct domain per theme, no overlap (see ARCHIVE.md's
// Pets section): each of the 9 base themes owns a single mechanical domain.
// Common→epic scale that theme's stat; the theme's legendary is a signature
// effect in the same domain, so no two themes ever share a buff. The two
// endgame categories introduce powers the base themes LACK: each Mythical
// (Astral) and Rainbow (Aurora) pet is a distinct new mechanic (crit, XP,
// ultimate charge, boss/minion damage, aura, execute, retribution, pierce,
// magnet), Rainbow tuned strictly above Mythical. Every unique effect id is
// distinct — the tests pin it (see pets.test.js).
export const RARITIES = {
  common:    { id: 'common',    label: 'Common',    color: '#9aa0b4', weight: 40 },
  uncommon:  { id: 'uncommon',  label: 'Uncommon',  color: '#5ad17a', weight: 28 },
  rare:      { id: 'rare',      label: 'Rare',      color: '#5a9fe6', weight: 17 },
  epic:      { id: 'epic',      label: 'Epic',      color: '#b05ae6', weight: 10 },
  legendary: { id: 'legendary', label: 'Legendary', color: '#f4c04d', weight: 5 },
  mythical:   { id: 'mythical', label: 'Mythical', color: '#ff5dcb', weight: 2 },
  rainbow:    { id: 'rainbow', label: 'Rainbow', color: '#fff59d', weight: 1 },
};

export const CATEGORIES = [
  { id: 'light',  label: 'Light' },
  { id: 'dark',   label: 'Dark' },
  { id: 'frost',  label: 'Frost' },
  { id: 'moss',   label: 'Moss' },
  { id: 'arcane', label: 'Arcane' },
  { id: 'fire',  label: 'Fire' },
  { id: 'water', label: 'Water' },
  { id: 'earth', label: 'Earth' },
  { id: 'wind',  label: 'Wind' },
  { id: 'mythical', label: 'Mythical — Astral' },
  { id: 'rainbow',  label: 'Rainbow — Aurora' },
];

// A pet's copies run from 1 (the base pull) to MAX_COPY_LEVEL (1 base + 5
// duplicates). Each duplicate adds COPY_LEVEL_STEP to a multiplier applied
// to that pet's bonus, so level 1 = 1.0x and level 6 = 2.0x. Duplicates
// pulled once a pet is already at MAX_COPY_LEVEL convert to essence instead
// (see SaveManager.recordPetPull).
export const MAX_COPY_LEVEL = 6;
export const COPY_LEVEL_STEP = 0.2;

export function getCopyMultiplier(level) {
  const clamped = Math.min(Math.max(level, 1), MAX_COPY_LEVEL);
  return 1 + COPY_LEVEL_STEP * (clamped - 1);
}

// `bonus` is either `{ stat, value, text }` (stat bonuses, aggregated by
// getPetBonuses, value scaled by getCopyMultiplier) or `{ unique, text }`
// (legendary+ one-off effects, wired up individually — see Game.js/
// CombatSystem/Player/PickupSystem/ProgressionSystem for each unique id's
// consumer and its base magnitude, also copy-level scaled).
export const PETS = [
  { id: 'lumen', name: 'Lumen', spriteKey: 'lumen', category: 'light', rarity: 'common', bonus: { stat: 'echoGain', value: 0.05, text: '+5% Echoes' } },
  { id: 'halo', name: 'Halo', spriteKey: 'halo', category: 'light', rarity: 'uncommon', bonus: { stat: 'echoGain', value: 0.08, text: '+8% Echoes' } },
  { id: 'beacon', name: 'Beacon', spriteKey: 'beacon', category: 'light', rarity: 'rare', bonus: { stat: 'echoGain', value: 0.12, text: '+12% Echoes' } },
  { id: 'sol', name: 'Sol', spriteKey: 'sol', category: 'light', rarity: 'epic', bonus: { stat: 'echoGain', value: 0.18, text: '+18% Echoes' } },
  { id: 'glimmer', name: 'Glimmer', spriteKey: 'glimmer', category: 'light', rarity: 'legendary', bonus: { unique: 'radiantInsight', text: 'Radiant Insight: level-ups offer a 4th choice' } },

  { id: 'gloom', name: 'Gloom', spriteKey: 'gloom', category: 'dark', rarity: 'common', bonus: { stat: 'damage', value: 0.03, text: '+3% damage' } },
  { id: 'murk', name: 'Murk', spriteKey: 'murk', category: 'dark', rarity: 'uncommon', bonus: { stat: 'damage', value: 0.05, text: '+5% damage' } },
  { id: 'umbra', name: 'Umbra', spriteKey: 'umbra', category: 'dark', rarity: 'rare', bonus: { stat: 'damage', value: 0.08, text: '+8% damage' } },
  { id: 'shade', name: 'Shade', spriteKey: 'shade', category: 'dark', rarity: 'epic', bonus: { stat: 'damage', value: 0.12, text: '+12% damage' } },
  { id: 'eclipse', name: 'Eclipse', spriteKey: 'eclipse', category: 'dark', rarity: 'legendary', bonus: { unique: 'bloodmoon', text: 'Bloodmoon: 10% lifesteal from direct hits (up to 20% at max copies)' } },

  { id: 'snowcap', name: 'Snowcap', spriteKey: 'snowcap', category: 'frost', rarity: 'common', bonus: { stat: 'dashCdPct', value: 0.08, text: '-8% dash cooldown' } },
  { id: 'rime', name: 'Rime', spriteKey: 'rime', category: 'frost', rarity: 'uncommon', bonus: { stat: 'dashCdPct', value: 0.12, text: '-12% dash cooldown' } },
  { id: 'frost', name: 'Frost', spriteKey: 'frost', category: 'frost', rarity: 'rare', bonus: { stat: 'dashCdPct', value: 0.18, text: '-18% dash cooldown' } },
  { id: 'glacier', name: 'Glacier', spriteKey: 'glacier', category: 'frost', rarity: 'epic', bonus: { stat: 'dashCdPct', value: 0.25, text: '-25% dash cooldown' } },
  { id: 'whiteout', name: 'Whiteout', spriteKey: 'whiteout', category: 'frost', rarity: 'legendary', bonus: { unique: 'permafrost', text: 'Permafrost: -30% dash cooldown (up to -60% at max copies)' } },

  { id: 'mossy', name: 'Mossy', spriteKey: 'mossy', category: 'moss', rarity: 'common', bonus: { stat: 'podHealing', value: 0.20, text: '+20% health-pod healing' } },
  { id: 'sprout', name: 'Sprout', spriteKey: 'sprout', category: 'moss', rarity: 'uncommon', bonus: { stat: 'podHealing', value: 0.35, text: '+35% health-pod healing' } },
  { id: 'grove', name: 'Grove', spriteKey: 'grove', category: 'moss', rarity: 'rare', bonus: { stat: 'podHealing', value: 0.50, text: '+50% health-pod healing' } },
  { id: 'bramble', name: 'Bramble', spriteKey: 'bramble', category: 'moss', rarity: 'epic', bonus: { stat: 'podHealing', value: 0.75, text: '+75% health-pod healing' } },
  { id: 'ancient', name: 'Ancient', spriteKey: 'ancient', category: 'moss', rarity: 'legendary', bonus: { unique: 'regrowth', text: 'Regrowth: health pods heal 100% more (up to +200% at max copies)' } },

  { id: 'ember', name: 'Ember', spriteKey: 'ember', category: 'arcane', rarity: 'common', bonus: { stat: 'fireRate', value: 0.05, text: '+5% weapon fire rate' } },
  { id: 'glyph', name: 'Glyph', spriteKey: 'glyph', category: 'arcane', rarity: 'uncommon', bonus: { stat: 'fireRate', value: 0.08, text: '+8% weapon fire rate' } },
  { id: 'sigil', name: 'Sigil', spriteKey: 'sigil', category: 'arcane', rarity: 'rare', bonus: { stat: 'fireRate', value: 0.12, text: '+12% weapon fire rate' } },
  { id: 'aether', name: 'Aether', spriteKey: 'aether', category: 'arcane', rarity: 'epic', bonus: { stat: 'fireRate', value: 0.18, text: '+18% weapon fire rate' } },
  { id: 'arcana', name: 'Arcana', spriteKey: 'arcana', category: 'arcane', rarity: 'legendary', bonus: { unique: 'overclock', text: 'Overclock: +20% weapon fire rate (up to +40% at max copies)' } },

  // Fire owns BURN (damage-over-time) — distinct from dark's raw damage. The
  // stat feeds the same ignite pool as Solaris's legendary (see CombatSystem).
  { id: 'cinder',  name: 'Cinder',  spriteKey: 'cinder',  category: 'fire', rarity: 'common',
    bonus: { stat: 'burn', value: 0.04, text: 'hits burn for +4% of damage over 3s' } },
  { id: 'scorch',  name: 'Scorch',  spriteKey: 'scorch',  category: 'fire', rarity: 'uncommon',
    bonus: { stat: 'burn', value: 0.07, text: 'hits burn for +7% of damage over 3s' } },
  { id: 'blaze',   name: 'Blaze',   spriteKey: 'blaze',   category: 'fire', rarity: 'rare',
    bonus: { stat: 'burn', value: 0.10, text: 'hits burn for +10% of damage over 3s' } },
  { id: 'pyre',    name: 'Pyre',    spriteKey: 'pyre',    category: 'fire', rarity: 'epic',
    bonus: { stat: 'burn', value: 0.14, text: 'hits burn for +14% of damage over 3s' } },
  { id: 'solaris', name: 'Solaris', spriteKey: 'solaris', category: 'fire', rarity: 'legendary',
    bonus: { unique: 'ignite', text: 'Ignite: hits burn for 18% of damage dealt over 3s (up to 36% at max copies)' } },

  { id: 'droplet',   name: 'Droplet',   spriteKey: 'droplet',   category: 'water', rarity: 'common',
    bonus: { stat: 'hpRegenPct', value: 0.003, text: '+0.3% max HP/sec' } },
  { id: 'ripple',    name: 'Ripple',    spriteKey: 'ripple',    category: 'water', rarity: 'uncommon',
    bonus: { stat: 'hpRegenPct', value: 0.005, text: '+0.5% max HP/sec' } },
  { id: 'tide',      name: 'Tide',      spriteKey: 'tide',      category: 'water', rarity: 'rare',
    bonus: { stat: 'hpRegenPct', value: 0.008, text: '+0.8% max HP/sec' } },
  { id: 'abyss',     name: 'Abyss',     spriteKey: 'abyss',     category: 'water', rarity: 'epic',
    bonus: { stat: 'hpRegenPct', value: 0.012, text: '+1.2% max HP/sec' } },
  { id: 'leviathan', name: 'Wellspring', spriteKey: 'leviathan', category: 'water', rarity: 'legendary',
    bonus: { unique: 'wellspring', text: 'Wellspring: dropping below half HP instantly restores 8% max HP (up to 16% at max copies; 6s cooldown)' } },

  { id: 'pebble',  name: 'Pebble',  spriteKey: 'pebble',  category: 'earth', rarity: 'common',
    bonus: { stat: 'maxHpPct', value: 0.15, text: '+15% max HP' } },
  { id: 'burrow',  name: 'Burrow',  spriteKey: 'burrow',  category: 'earth', rarity: 'uncommon',
    bonus: { stat: 'maxHpPct', value: 0.25, text: '+25% max HP' } },
  { id: 'boulder', name: 'Boulder', spriteKey: 'boulder', category: 'earth', rarity: 'rare',
    bonus: { stat: 'maxHpPct', value: 0.40, text: '+40% max HP' } },
  { id: 'terra',   name: 'Terra',   spriteKey: 'terra',   category: 'earth', rarity: 'epic',
    bonus: { stat: 'maxHpPct', value: 0.60, text: '+60% max HP' } },
  { id: 'titan',   name: 'Titan',   spriteKey: 'titan',   category: 'earth', rarity: 'legendary',
    bonus: { unique: 'stoneskin', text: 'Stoneskin: -12% damage taken (up to -24% at max copies)' } },

  { id: 'breeze',    name: 'Breeze',    spriteKey: 'breeze',    category: 'wind', rarity: 'common',
    bonus: { stat: 'moveSpeed', value: 0.03, text: '+3% move speed' } },
  { id: 'gust',      name: 'Gust',      spriteKey: 'gust',      category: 'wind', rarity: 'uncommon',
    bonus: { stat: 'moveSpeed', value: 0.05, text: '+5% move speed' } },
  { id: 'zephyr',    name: 'Zephyr',    spriteKey: 'zephyr',    category: 'wind', rarity: 'rare',
    bonus: { stat: 'moveSpeed', value: 0.08, text: '+8% move speed' } },
  { id: 'tempest',   name: 'Tempest',   spriteKey: 'tempest',   category: 'wind', rarity: 'epic',
    bonus: { stat: 'moveSpeed', value: 0.12, text: '+12% move speed' } },
  { id: 'maelstrom', name: 'Slipstream', spriteKey: 'maelstrom', category: 'wind', rarity: 'legendary',
    bonus: { unique: 'slipstream', text: 'Slipstream: after dashing, +40% move speed & +50% dash range for 2s (up to +80% / +100% at max copies)' } },

  // Mythical (Astral) pets each introduce a NEW power the base themes lack —
  // no overlap with any theme's stat or legendary. Sidegrade-balanced: each is
  // best for a different build, all comparable in power, above the legendaries.
  { id: 'mythic-moss', name: 'Mythic Sage', spriteKey: 'mythicMoss', category: 'mythical', rarity: 'mythical',
    bonus: { unique: 'fastStudy', text: 'Fast Study: +25% XP gain (up to +50% at max copies)' } },
  { id: 'mythic-flare', name: 'Mythic Edge', spriteKey: 'mythicFlare', category: 'mythical', rarity: 'mythical',
    bonus: { unique: 'keenEdge', text: 'Keen Edge: +12% crit chance, crits deal +40% damage (up to +24% / +80% at max copies)' } },
  { id: 'mythic-depth', name: 'Mythic Scourge', spriteKey: 'mythicDepth', category: 'mythical', rarity: 'mythical',
    bonus: { unique: 'swarmbane', text: 'Swarmbane: +25% damage to non-boss enemies (up to +50% at max copies)' } },
  { id: 'mythic-core', name: 'Mythic Slayer', spriteKey: 'mythicCore', category: 'mythical', rarity: 'mythical',
    bonus: { unique: 'giantSlayer', text: 'Giant Slayer: +25% damage to bosses (up to +50% at max copies)' } },
  { id: 'mythic-storm', name: 'Mythic Surge', spriteKey: 'mythicStorm', category: 'mythical', rarity: 'mythical',
    bonus: { unique: 'overdrive', text: 'Overdrive: +30% ultimate charge rate (up to +60% at max copies)' } },

  // Rainbow (Aurora) pets are the top tier — the flashiest NEW powers, each
  // distinct from every base theme, legendary, AND mythical, and tuned
  // strictly above the mythicals. Sidegrade-balanced within the tier.
  { id: 'rainbow-aurora', name: 'Rainbow Reaper', spriteKey: 'rainbowAurora', category: 'rainbow', rarity: 'rainbow',
    bonus: { unique: 'judgment', text: 'Judgment: instantly finishes non-boss enemies below 15% HP (up to 30% at max copies)' } },
  { id: 'rainbow-inferno', name: 'Rainbow Corona', spriteKey: 'rainbowInferno', category: 'rainbow', rarity: 'rainbow',
    bonus: { unique: 'searingAura', text: 'Searing Corona: a ring of flame burns nearby enemies for 25/s (up to 50/s at max copies)' } },
  { id: 'rainbow-tide', name: 'Rainbow Riptide', spriteKey: 'rainbowTide', category: 'rainbow', rarity: 'rainbow',
    bonus: { unique: 'riptide', text: 'Riptide: health pods are pulled to you from anywhere (faster at max copies)' } },
  { id: 'rainbow-monolith', name: 'Rainbow Nemesis', spriteKey: 'rainbowMonolith', category: 'rainbow', rarity: 'rainbow',
    bonus: { unique: 'retribution', text: 'Retribution: taking a hit blasts nearby enemies for 25 damage (up to 50 at max copies)' } },
  { id: 'rainbow-tempest', name: 'Rainbow Lance', spriteKey: 'rainbowTempest', category: 'rainbow', rarity: 'rainbow',
    bonus: { unique: 'piercingGale', text: 'Piercing Gale: your shots pierce +1 extra enemy (up to +2 at max copies)' } },
];

/**
 * Aggregates the run bonuses granted by the currently EQUIPPED pets only
 * (max 2 — see SaveManager.maxPetSlots). `ownedPetLevels` is the save's
 * `ownedPets` map (petId -> copy level 1-6); each equipped pet's stat value
 * or unique magnitude is scaled by getCopyMultiplier(level) before being
 * summed/collected. Stat bonuses are additive across the (at most two)
 * equipped pets and returned as one bracket per stat — every pet stat is now
 * percentage-based, returned either as a `1+sum` multiplier (damage, move
 * speed, echoes, max HP, pod heal, fire rate) or as a raw fraction the
 * consumer applies (burn %, HP regen % of max HP, dash cooldown %); Game.js
 * multiplies/adds these alongside the meta-shop and character brackets (which
 * keep their own flat pieces). `uniques` maps each
 * equipped unique effect's id to its copy-level multiplier (1.0-2.0), so
 * consumers can scale their own base magnitude; a `.has`/`.get(...)!==
 * undefined` check alone still works for presence-only (non-scaling) effects.
 */
export function getPetBonuses(equippedIds, ownedPetLevels = {}) {
  const sums = {
    damage: 0, moveSpeed: 0, echoGain: 0, maxHpPct: 0, hpRegenPct: 0,
    dashCdPct: 0, podHealing: 0, fireRate: 0, burn: 0,
  };
  const uniques = new Map();
  for (const id of equippedIds ?? []) {
    const pet = PETS.find((p) => p.id === id);
    if (!pet) continue;
    const multiplier = getCopyMultiplier(ownedPetLevels[id] ?? 1);
    if (pet.bonus.unique) uniques.set(pet.bonus.unique, multiplier);
    else sums[pet.bonus.stat] += pet.bonus.value * multiplier;
  }
  return {
    damageMultiplier: 1 + sums.damage,
    moveSpeedMultiplier: 1 + sums.moveSpeed,
    echoGainMultiplier: 1 + sums.echoGain,
    // Earth/water/frost are percentage-based (scale with the build), not flat:
    // maxHp as a multiplier, HP regen as a fraction of max HP per second, dash
    // cooldown as a fraction of the base cooldown. Game.js folds each in.
    maxHpMultiplier: 1 + sums.maxHpPct,
    hpRegenPercent: sums.hpRegenPct,
    dashCooldownPct: sums.dashCdPct,
    podHealMultiplier: 1 + sums.podHealing,
    fireRateMultiplier: 1 + sums.fireRate,
    // Fire's burn stat: fraction of each hit's damage banked as an ignite
    // burn, additive across equipped fire pets and folded into
    // combat.ignitePercent alongside Solaris's Ignite legendary (see Game.js).
    petBurnPercent: sums.burn,
    uniques,
  };
}

// Boosted weights for the "Fated Pull" Shard unlock (data/shardUnlocks.js's
// luckySummon) — everything rare-or-better, common/uncommon untouched.
// Deliberately NOT a single flat multiplier applied to every tier: a
// uniform 2.15x once pushed Rare's boosted weight (17 * 2.15 = 36.55) past
// Uncommon's plain 28, meaning a "lucky" pull made Rare pets *more* common
// than Uncommon ones — backwards. Each tier below instead gets its own
// weight, individually capped below the next-more-common tier's weight so
// the full ordering (common > uncommon > rare > epic > legendary >
// mythical > rainbow) always holds, boosted or not. Rainbow is tuned to
// land close to a clean 1.5% of the total once luckyOdds is active;
// recompute the rest by hand if RARITIES' base weights ever change.
const LUCKY_ODDS_WEIGHTS = { rare: 24, epic: 16, legendary: 8, mythical: 3, rainbow: 1.8 };

/**
 * Rolls one summon-eligible pet. Duplicate protection deliberately lives in
 * SaveManager.recordPetPull(), so the gacha can keep producing duplicates
 * after every pet is at max copy level. Mythical/Rainbow pets are weighted
 * as rare endgame pulls. `luckyOdds` (see LUCKY_ODDS_WEIGHTS above) swaps in
 * a higher weight for every rare-or-better tier before picking, which both
 * raises their odds and proportionally lowers common/uncommon's share of
 * the (now larger) total — without ever inverting the rarity ordering.
 */
export function rollPet({ luckyOdds = false } = {}) {
  const pool = PETS.filter((pet) => RARITIES[pet.rarity].weight > 0);

  const tiers = Object.values(RARITIES).filter((r) => r.weight > 0 && pool.some((pet) => pet.rarity === r.id));
  const effectiveWeight = (r) => (luckyOdds && LUCKY_ODDS_WEIGHTS[r.id] ? LUCKY_ODDS_WEIGHTS[r.id] : r.weight);
  const totalWeight = tiers.reduce((sum, r) => sum + effectiveWeight(r), 0);
  let pick = Math.random() * totalWeight;
  let tier = tiers[tiers.length - 1];
  for (const r of tiers) {
    pick -= effectiveWeight(r);
    if (pick < 0) { tier = r; break; }
  }

  const tierPool = pool.filter((pet) => pet.rarity === tier.id);
  return tierPool[Math.floor(Math.random() * tierPool.length)];
}
